import { CustomModal } from "../custom-modal/custom-modal";
import ErrorBoundary from "../error-boundary/error-boundary";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "../custom-breadcrumb/custom-breadcrumb";
import { HELPER } from "../../helper/helper";
import { CustomLoader } from "../custom-loader/custom-loader";
import { CustomTable } from "../custom-table/custom-table";
import { TransactionDetails } from "./transaction-details";
import { TransactionsFilter } from "./transactions-filter";
import { SERVICES } from "../../../core/services/services";
import { AccessDenied } from "../../../components/access-denied/access-denied";
import { UserTypes } from "../../constants";
import { API } from "../../../core/apis/apis";
import { Optional } from "../optional/optional";
import { saveAs } from "file-saver";

export function Transactions(props) {
  const toast = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [error, setError] = useState(null);
  const [isSearch, setIsSearch] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedTransactionStatus, setSelectedTransactionStatus] =
    useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [transactionReference, setTransactionReference] = useState(null);
  const [authority, setAuthority] = useState("bo_dd_get_all_transactions");
  const [isOnMandateDetailsView, setIsOnMandateDetailsView] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const tableHeaders = [
    { label: "Transaction Amount", value: "amount" },
    { label: "Transaction Date Time", value: "transactionTime" },
    { label: "Payment Facilitator", value: "paymentFacilitatorName" },
    { label: "Transaction Status", value: "status" },
    { label: "Response Code", value: "responseCode" },
    { label: "Transaction Reference", value: "transactionReference" },
    { label: "Actions", value: "actions" },
  ];

  const getApiUrl = useCallback(() => {
    if (props.userType === UserTypes.Frontoffice) {
      setAuthority("fo_dd_get_all_transactions");
      return API.GET_FRONT_OFFICE_TRANSACTIONS;
    } else {
      return API.GET_TRANSACTIONS;
    }
  }, [props.userType]);

  const getTransactionDetails = useCallback((e) => {
    setTransactionReference(e?.transactionReference);
    setSelectedTransactionStatus(e?.status);
    setCurrentIndex(2);
  }, []);

  const getTransactions = useCallback(() => {
    const url = getApiUrl();
    setTransactions([]);
    setCurrentIndex(0);
    const params = {
      page: pageNo,
      size: 10,
      // direction: "DESC",
    };
    const payload = searchParams ? searchParams : {};
    setIsSearch(searchParams ? true : false);
    SERVICES.MAKE_PUT_REQUEST(url, payload, HELPER.TO_URL_STRING(params))
      .then((data) => {
        const result = data?.result?.content;
        if (!result.length) {
          if (isSearch) {
            setIsSearch(true);
          }
        } else {
          let arr = [];
          setTotalItems(data?.result.totalElements);
          setTotalPages(data?.result.totalPages);
          result.forEach((e) => {
            arr.push({
              ...e,
              actions: "CR",
              detailsFunction: getTransactionDetails,
            });
          });
          setTransactions(arr);
        }
        setError(null);
        setTimeout(() => {
          setCurrentIndex(1);
        }, 1000);
      })
      .catch((error) => {
        setError(HELPER.PROCESS_ERROR(error));
      });
  }, [pageNo, searchParams, getApiUrl, getTransactionDetails, isSearch]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  function toggleModal() {
    setShowModal(!showModal);
  }

  function saveSearchParams(params) {
    setPageNo(0);
    setSearchParams(params);
    toggleModal();
  }

  function reload() {
    setPageNo(0);
    setTransactions([]);
    setSearchParams(null);
    setIsSearch(false);
    // setCurrentIndex(0);
    getTransactions();
  }

  function downloadTransactionsCsv(payloadOverride = null) {
    if (currentIndex !== 1 || isDownloading) {
      return;
    }
    const payload =
      payloadOverride !== null
        ? payloadOverride
        : searchParams
        ? searchParams
        : {};

        
    setIsDownloading(true);
    const downloadRequest =
      props.userType === UserTypes.Frontoffice
        ? () => SERVICES.DOWNLOAD_DIRECT_DEBIT_TRANSACTIONS(payload, "fo")
        : () => SERVICES.DOWNLOAD_DIRECT_DEBIT_TRANSACTIONS(payload);

    downloadRequest()
      .then((data) => {
        const today = new Date().toISOString().slice(0, 10);
        saveAs(data, `transactions-${today}.csv`);
      })
      .catch(async (error) => {
        const status = error?.status || error?.response?.status;
        let errMessage = "";
        if (status === 403) {
          errMessage = "You do not have permission to export CSV.";
        } else {
          errMessage =
            "Unable to download CSV at the moment. Please try again later.";
        }
        toast.current?.show({
          severity: "error",
          summary: "Download failed",
          detail: errMessage,
          life: 10000,
        });
      })
      .finally(() => setIsDownloading(false));
  }

  const modalContent = () => {
    return (
      <TransactionsFilter
        searchFunction={saveSearchParams}
        closeModal={toggleModal}
        userType={props.userType}
        onDownloadCsv={downloadTransactionsCsv}
        canDownloadCsv={
          HELPER.HAS_AUTHORITY(authority) &&
          currentIndex === 1 &&
          HELPER.HAS_ROLE(["DD_BANK_ADMIN", "DD_PAYMENT_FACILITATOR_ADMIN"])
        }
        isDownloading={isDownloading}
      />
    );
  };

  const transactionsView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading transactions..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            emptyText={"No transactions found!"}
            search={isSearch}
            reload={reload}
            error={error}
            items={transactions}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={(pageNo) => setPageNo(pageNo)}
          />
        );
      case 2:
        return (
          <div className="p-mt-2">
            <TransactionDetails
              responseStatus={selectedTransactionStatus}
              transactionReference={transactionReference}
              goBack={() => setCurrentIndex(1)}
              userType={props.userType}
              onShowMandateDetails={() => setIsOnMandateDetailsView(true)}
              onHideMandateDetails={() => setIsOnMandateDetailsView(false)}
              // toggleBreadCrumbView={toggleBreadCrumbView}
            />
          </div>
        );
      case 3:
        return (
          <div className="mobile-modal-container">
            <div className="custom-card">
              <div className="mobile-card-position"></div>
            </div>
          </div>
        );
    }
  };

  return (
    <ErrorBoundary>
      <div>
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={toggleModal}
            onHide={toggleModal}
            visible={showModal}
            modalContent={modalContent}
          />
        </div>
        <Optional show={currentIndex !== 2}>
          <div className="page-title p-text-left">
            {isOnMandateDetailsView ? "Mandate" : "Transactions"}
          </div>
          <div className="p-mt-2">
            <CustomBreadcrumb
              page={`${
                isOnMandateDetailsView
                  ? "Manage Mandate"
                  : "Manage Transactions"
              }`}
            />
          </div>
        </Optional>
        <div className="floating-buttons desktop-scree">
          <div className="p-grid">
            <div className="p-col-7"></div>
            <div className="p-col-5">
              <div className="p-grid">
                <div className="p-col-12 text-end">
                  <div
                    className={
                      HELPER.HAS_AUTHORITY(authority) && currentIndex === 1
                        ? "dcir-show"
                        : "dcir-hide"
                    }
                  >
                    <button
                      // disabled={loading || transactions?.length === 0}
                      onClick={toggleModal}
                      className="primary-button"
                    >
                      <i className="pi pi-filter" />
                      <span className="hide-btn-text"> Filter</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={
            HELPER.HAS_AUTHORITY(authority)
              ? "dcir-show"
              : HELPER.HAS_ROLE(["DD_BANK_ADMIN", "DD_PAYMENT_FACILITATOR_ADMIN"])
              ? "dcir-show"
              : "dcir-hide"
          }
        >
          {transactionsView()}
        </div>
        <div
          className={
            HELPER.HAS_AUTHORITY(authority)
              ? "dcir-hide"
              : HELPER.HAS_ROLE(["DD_BANK_ADMIN", "DD_PAYMENT_FACILITATOR_ADMIN"])
              ? "dcir-hide"
              : "dcir-show"
          }
        >
          <AccessDenied />
        </div>
      </div>
    </ErrorBoundary>
  );
}
